import { Event, EventId, EventTitle, EventSubtitle, EventInstructor, EventDate, EventTime } from '../../domain/entities/Event';
import { EventRepository } from '../../domain/repositories/EventRepository';
import { GoogleSheetsService, SpreadsheetData } from '../services/GoogleSheetsService';
import { GOOGLE_SHEETS_CONFIG } from '../../shared/constants/GoogleSheetsConfig';

export class GoogleSheetsEventRepositoryImpl implements EventRepository {
  private sheetsService: GoogleSheetsService;

  constructor() {
    // Usar gid=0 para a primeira aba (Eventos)
    this.sheetsService = new GoogleSheetsService(GOOGLE_SHEETS_CONFIG.SPREADSHEET_ID, '0');
  }

  async findAll(): Promise<Event[]> {
    try {
      const spreadsheetData = await this.sheetsService.loadSpreadsheet();
      
      if (!spreadsheetData) {
        console.warn('Não foi possível carregar dados da planilha');
        return [];
      }

      const events = this.parseSpreadsheetData(spreadsheetData);
      console.log(`Eventos carregados da planilha: ${events.length}`);
      return events;
    } catch (error) {
      console.error('Erro ao carregar eventos da planilha:', error);
      return [];
    }
  }

  async findUpcoming(): Promise<Event[]> {
    const allEvents = await this.findAll();
    console.log('Todos os eventos carregados:', allEvents.length);
    
    const upcomingEvents = allEvents.filter(event => {
      const isUpcoming = event.isUpcoming();
      console.log(`Evento "${event.title.value}" - Data: ${event.date.value}, É futuro: ${isUpcoming}`);
      return isUpcoming;
    });
    
    console.log(`Eventos futuros encontrados: ${upcomingEvents.length}`);
    return upcomingEvents;
  }

  async findById(id: string): Promise<Event | null> {
    const allEvents = await this.findAll();
    return allEvents.find(event => event.id.value === id) || null;
  }

  private parseSpreadsheetData(data: SpreadsheetData): Event[] {
    const events: Event[] = [];
    
    if (!data.table || !data.table.rows) {
      console.warn('Estrutura de dados inválida');
      return events;
    }

    console.log('Total de linhas na planilha:', data.table.rows.length);
    console.log('Estrutura das colunas:', data.table.cols);

    // Processar todas as linhas, mas pular cabeçalhos se necessário
    for (let i = 0; i < data.table.rows.length; i++) {
      const row = data.table.rows[i];
      
      console.log(`Processando linha ${i}:`, row);
      
      if (!row.c || row.c.length < 4) {
        console.warn(`Linha ${i} tem dados insuficientes:`, row.c);
        continue;
      }

      // Verificar se é cabeçalho (primeira linha com títulos das colunas)
      const firstCell = row.c[0]?.v?.toString() || '';
      if (i === 0 && (firstCell.toLowerCase() === 'titulo' || firstCell.toLowerCase() === 'title')) {
        console.log(`Pulando cabeçalho na linha ${i}`);
        continue;
      }

      try {
        const event = this.parseRowToEvent(row, i);
        if (event) {
          events.push(event);
          console.log('Evento criado com sucesso:', event.title.value);
        } else {
          console.warn(`Linha ${i} não gerou evento válido`);
        }
      } catch (error) {
        console.error(`Erro ao processar linha ${i}:`, error);
      }
    }

    console.log('Total de eventos processados:', events.length);
    return events;
  }

  private parseRowToEvent(row: { c: Array<{ v: string | number | null; f?: string }> }, rowNumber: number): Event | null {
    const cells = row.c;
    
    console.log(`Parseando linha ${rowNumber} - células:`, cells);
    
    // Mapear os campos: [Titulo, Subtitulo, Instrutor, Data, Hora, HoraFim, URL]
    const title = cells[0]?.v?.toString() || '';
    const subtitle = cells[1]?.v?.toString() || '';
    const instructor = cells[2]?.v?.toString() || '';
    const dateCell = cells[3];
    const timeCell = cells[4];
    const endTimeCell = cells[5];

    console.log(`Campos extraídos - Título: "${title}", Subtítulo: "${subtitle}", Instrutor: "${instructor}"`);
    console.log(`Célula de data:`, dateCell);
    console.log(`Célula de hora:`, timeCell);
    console.log(`Célula de hora fim:`, endTimeCell);

    if (!title || !dateCell) {
      console.warn(`Linha ${rowNumber} tem dados obrigatórios ausentes - título: "${title}", data:`, dateCell);
      return null;
    }

    // Parse da data - usar o valor formatado (f) se disponível
    let eventDate: Date;
    try {
      eventDate = this.parseGoogleSheetsDate(dateCell);
      console.log(`Data parseada:`, eventDate);
    } catch (error) {
      console.warn(`Erro ao processar data na linha ${rowNumber}:`, error);
      return null;
    }

    // Parse da hora - usar o valor formatado (f) se disponível
    let timeStr = '19:00';
    if (timeCell) {
      timeStr = this.parseGoogleSheetsTime(timeCell);
      console.log(`Hora parseada:`, timeStr);
    }

    // Parse da hora de término (opcional)
    let endTimeStr: string | undefined = undefined;
    if (endTimeCell) {
      const parsedEndTime = this.parseGoogleSheetsTime(endTimeCell);
      if (parsedEndTime && parsedEndTime !== '') {
        endTimeStr = parsedEndTime;
        console.log(`Hora de término parseada:`, endTimeStr);
      }
    }

    const event = new Event(
      { value: `event-${rowNumber}` } as EventId,
      { value: title.trim() } as EventTitle,
      { value: subtitle.trim() } as EventSubtitle,
      { value: instructor.trim() } as EventInstructor,
      { value: eventDate } as EventDate,
      { value: timeStr } as EventTime,
      endTimeStr ? { value: endTimeStr } : undefined
    );

    console.log(`Evento criado:`, event);
    return event;
  }

  private parseGoogleSheetsDate(dateCell: { v: string | number | null; f?: string }): Date {
    // Priorizar o valor formatado (f) que está correto
    if (dateCell.f) {
      console.log('Usando valor formatado para data:', dateCell.f);
      return this.parseDate(dateCell.f);
    }
    
    // Se temos o valor bruto (v), pode ser um objeto Date do Google Sheets
    if (dateCell.v && typeof dateCell.v === 'string' && dateCell.v.startsWith('Date(')) {
      console.log('Usando valor bruto para data:', dateCell.v);
      return this.parseGoogleSheetsDateString(dateCell.v);
    }
    
    // Fallback para parse direto
    console.log('Usando fallback para data:', dateCell.v);
    return this.parseDate(dateCell.v?.toString() || '');
  }

  private parseGoogleSheetsTime(timeCell: { v: string | number | null; f?: string }): string {
    if (timeCell.f) {
      const f = timeCell.f.trim();
      return f.length >= 5 ? f.substring(0, 5) : f;
    }

    if (timeCell.v && typeof timeCell.v === 'string' && timeCell.v.startsWith('Date(')) {
      return this.parseGoogleSheetsTimeString(timeCell.v);
    }

    if (typeof timeCell.v === 'number') {
      const totalMinutes = Math.round(timeCell.v * 24 * 60);
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    }

    const fallback = timeCell.v?.toString().trim() || '19:00';
    return fallback.length >= 5 ? fallback.substring(0, 5) : fallback;
  }

  private parseGoogleSheetsDateString(dateStr: string): Date {
    // Formato: Date(2025,6,20) - ano, mês (0-based), dia
    const match = dateStr.match(/Date\((\d+),(\d+),(\d+)\)/);
    if (match) {
      const [, year, month, day] = match;
      // O mês no Google Sheets é 0-based, mas o Date() do JavaScript também é 0-based
      // Então não precisamos subtrair 1
      return new Date(parseInt(year), parseInt(month), parseInt(day));
    }
    throw new Error(`Formato de data do Google Sheets não reconhecido: ${dateStr}`);
  }

  private parseGoogleSheetsTimeString(timeStr: string): string {
    // Formato: Date(1899,11,30,19,0,0) - ano, mês, dia, hora, minuto, segundo
    const match = timeStr.match(/Date\(\d+,\d+,\d+,(\d+),(\d+),\d+\)/);
    if (match) {
      const [, hour, minute] = match;
      return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
    }
    return '19:00';
  }

  private parseDate(dateStr: string): Date {
    const cleanDateStr = dateStr.trim();
    
    console.log('=== INÍCIO DO PARSE DE DATA ===');
    console.log('String de entrada:', cleanDateStr);
    console.log('Tipo da string:', typeof cleanDateStr);
    console.log('Comprimento da string:', cleanDateStr.length);
    
    // Tentar diferentes formatos
    const dateFormats = [
      { pattern: /(\d{1,2})\/(\d{1,2})\/(\d{4})/, name: 'DD/MM/YYYY' }, // DD/MM/YYYY
      { pattern: /(\d{4})-(\d{1,2})-(\d{1,2})/, name: 'YYYY-MM-DD' },   // YYYY-MM-DD
      { pattern: /(\d{1,2})-(\d{1,2})-(\d{4})/, name: 'DD-MM-YYYY' },   // DD-MM-YYYY
    ];
    
    for (const format of dateFormats) {
      console.log(`Testando formato ${format.name} com regex:`, format.pattern);
      const match = cleanDateStr.match(format.pattern);
      console.log(`Resultado do match:`, match);
      if (match) {
        if (format.name === 'DD/MM/YYYY') {
          const [, day, month, year] = match;
          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          return date;
        } else if (format.name === 'YYYY-MM-DD') {
          const [, year, month, day] = match;
          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          return date;
        } else {
          const [, day, month, year] = match;
          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          return date;
        }
      }
    }
    // Tentar parse direto
    const parsed = new Date(cleanDateStr);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
    throw new Error(`Formato de data não reconhecido: ${dateStr}`);
  }
}